import {isBrowserContext} from 'core/helpers';
import {
    FilterType,
    FilterValuesJoinType,
    IFilter,
    IFilterFacet,
    IFilterFacetVariant,
} from 'plugins/modules/filter/types';

interface IAppliedFilterOrderItem {
    filterCode: string;
    variantCode: string | null;
}

export interface IAppliedFilter {
    filter: IFilterFacet;
    variant: IFilterFacetVariant | null;
}

export class AppliedFilters {
    private constructor() {
        if (!isBrowserContext()) {
            throw new Error('Данный класс предназначен для работы только в браузере!');
        }
    }

    private static instance: AppliedFilters;
    private appliedFiltersSortedList: IAppliedFilterOrderItem[] = [];

    private addToAppliedFiltersSortedList = (filterCode: string, variantCode: string | null) => {
        this.removeFromAppliedFiltersSortedList(filterCode, variantCode);

        this.appliedFiltersSortedList.push({
            filterCode,
            variantCode,
        });
    };

    static getInstance = () => {
        if (!AppliedFilters.instance) {
            AppliedFilters.instance = new AppliedFilters();
        }

        return AppliedFilters.instance;
    };

    private getAppliedFilterOrderIndex = (data: IAppliedFilter) => {
        return this.appliedFiltersSortedList.findIndex(({filterCode, variantCode}) => {
            const {filter, variant} = data;

            if (!variant) {
                return filterCode === filter.code;
            }

            return filterCode === filter.code && variantCode === String(variant.code);
        });
    };

    private removeFromAppliedFiltersSortedList = (filterCode: string, variantCode: string | null) => {
        if (!variantCode) {
            this.appliedFiltersSortedList = this.appliedFiltersSortedList.filter(
                (item) => item.filterCode !== filterCode,
            );

            return;
        }

        this.appliedFiltersSortedList = this.appliedFiltersSortedList.filter((item) => {
            return item.filterCode !== filterCode || item.variantCode !== String(variantCode);
        });
    };

    private sortAppliedFilters = (appliedFilters: IAppliedFilter[]) => {
        return appliedFilters.sort((a, b) => {
            const indexA = this.getAppliedFilterOrderIndex(a);
            const indexB = this.getAppliedFilterOrderIndex(b);

            return indexA - indexB;
        });
    };

    addAppliedFilter = (filterCode: string, variantCode: string | null) => {
        this.addToAppliedFiltersSortedList(filterCode, variantCode);
    };

    getAppliedFilters = (currentFilters: IFilter[], filters: IFilterFacet[]): IAppliedFilter[] => {
        const appliedFilters: IAppliedFilter[] = [];

        currentFilters.forEach((currentFilter) => {
            const filter = filters.find(({code}) => {
                return currentFilter.code === code;
            });

            if (!filter) {
                return;
            }

            const {type} = filter;
            const isRangeFilter = type === FilterType.RANGE || type === FilterType.RANGE_WITH_GROUP;

            if (isRangeFilter) {
                appliedFilters.push({
                    filter,
                    variant: null,
                });

                return;
            }

            const variants = filter.variants?.filter(({code}) => {
                return currentFilter.values.some((value) => value === String(code));
            });

            variants?.forEach((variant) => {
                appliedFilters.push({
                    filter,
                    variant,
                });
            });
        });

        return this.sortAppliedFilters(appliedFilters);
    };

    removeAllAppliedFilters = () => {
        this.appliedFiltersSortedList = [];
    };

    removeAppliedFilter = (filterCode: string, variantCode: string | null) => {
        this.removeFromAppliedFiltersSortedList(filterCode, variantCode);
    };

    // для коррекции значений при навигации в браузере через кнопки вперед и назад
    updateAppliedFiltersOrder = (currentFilters: IFilter[]) => {
        // убираем значения, которых нет в currentFilters
        this.appliedFiltersSortedList = this.appliedFiltersSortedList.filter(({filterCode, variantCode}) => {
            const currentFilter = currentFilters.find(({code}) => code === filterCode);

            if (!currentFilter) {
                return false;
            }

            if (!variantCode) {
                return true;
            }

            return currentFilter.values.some((code) => String(code) === variantCode);
        });

        // проверяем, есть ли в currentFilters новые значения
        currentFilters.forEach(({code, values, valuesJoinType}) => {
            if (valuesJoinType === FilterValuesJoinType.RANGE) {
                const appliedFilter = this.appliedFiltersSortedList.find((item) => item.filterCode === code);

                if (!appliedFilter) {
                    this.addToAppliedFiltersSortedList(code, null);
                }

                return;
            }

            values.forEach((variantCode) => {
                const appliedFilter = this.appliedFiltersSortedList.find((item) => {
                    return item.filterCode === code && item.variantCode === String(variantCode);
                });

                if (!appliedFilter) {
                    this.addToAppliedFiltersSortedList(code, variantCode ?? '');
                }
            });
        });
    };
}
